import { useMemo } from "react";

import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@/hooks/useFeatures";
import type { LoginInfo } from "@/types/LoginInfo";

/**
 * STIC-15365 — the one place that decides whether a login has the end-user
 * Home menu at all.
 *
 * | login   | Home menu reachable                                  |
 * |---------|------------------------------------------------------|
 * | NORMAL  | always                                               |
 * | PARTNER | only with the `legacy_partner_home_menu` feature key |
 * | SYSTEM  | never                                                |
 *
 * "Reachable" is stronger than "has the Home button": when this is false the
 * Home routes must not resolve to the Home menu either — see rules 4 and 7 of
 * `SidebarBody.getMenuByLocationPath`. The partner variant
 * (channel vs multi-org) is deliberately not an input: the feature key is the
 * behaviour axis, the variant only decides which plan carries the key by
 * default.
 *
 * Plain function so non-hook callers (`LandingPageHelper`) can use it; the
 * hook below is the same predicate memoised for components.
 */
export const isHomeMenuReachable = (loginInfo: LoginInfo | null | undefined): boolean => {
  switch (loginInfo?.organisation?.organisationType) {
    case "SYSTEM":
      return false;
    case "PARTNER":
      return hasFeature(FeatureKey.legacy_partner_home_menu, loginInfo);
    default:
      return true;
  }
};

export interface NavigationModel {
  /** See {@link isHomeMenuReachable}. */
  homeMenuReachable: boolean;
  isSystemLogin: boolean;
  /** Either partner kind — channel or multi-org. */
  isPartnerLogin: boolean;
}

const useNavigationModel = (loginInfo: LoginInfo | null | undefined): NavigationModel => {
  const organisationType = loginInfo?.organisation?.organisationType;
  // hasFeature reads the features map off loginInfo (not the store) so the memo
  // keys on the same object the caller re-renders on.
  const homeMenuReachable = isHomeMenuReachable(loginInfo);

  return useMemo(
    (): NavigationModel => ({
      homeMenuReachable,
      isSystemLogin: organisationType === "SYSTEM",
      isPartnerLogin: organisationType === "PARTNER",
    }),
    [organisationType, homeMenuReachable]
  );
};

export default useNavigationModel;
